import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CreditCard } from 'lucide-react';
import { useConfirmPayment, useListPaymentMethods } from '@/hooks/payment';
import { Payment, PaymentMethod, UserPath, PaymentPath } from '@generated/proto/model';
import { PaymentMethodSelector } from './PaymentMethodSelector';

interface CheckoutFormProps {
  payment: Payment;
  userPath: UserPath;
  onSuccess?: (paymentId: string) => void;
  onCancel?: () => void;
}

export function CheckoutForm({
  payment,
  userPath,
  onSuccess,
  onCancel,
}: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { confirmPayment, loading: isConfirming } = useConfirmPayment();
  const { data: paymentMethods = [] } = useListPaymentMethods(userPath);
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [useNewCard, setUseNewCard] = useState(false);

  // Select default payment method on load
  useEffect(() => {
    const defaultMethod = paymentMethods.find(pm => pm.isDefault);
    if (defaultMethod) {
      setSelectedPaymentMethod(defaultMethod);
    } else if (paymentMethods.length === 1) {
      setSelectedPaymentMethod(paymentMethods[0]);
    }
  }, [paymentMethods]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !payment.clientSecret) {
      setError('Payment setup incomplete. Please try again.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let paymentMethodId = selectedPaymentMethod?.paymentMethodId;

      // If using new card, create payment method first
      if (useNewCard) {
        const cardElement = elements?.getElement(CardElement);
        if (!cardElement) {
          setError('Card element not found.');
          return;
        }

        const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        });

        if (stripeError) {
          setError(stripeError.message || 'Failed to process card');
          return;
        }

        paymentMethodId = paymentMethod?.id;
      }

      // Confirm payment with backend
      const response = await confirmPayment(
        payment.tripId,
        payment.paymentId,
        paymentMethodId
      );

      if (response.requiresAction) {
        // Handle additional authentication (3D Secure, etc.)
        const { error } = await stripe.confirmPayment({
          clientSecret: payment.clientSecret,
          confirmParams: {
            return_url: window.location.href,
          },
        });

        if (error) {
          setError(error.message || 'Payment confirmation failed');
          return;
        }
      }

      onSuccess?.(payment.paymentId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment failed');
    } finally {
      setIsLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Complete Payment
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Payment Summary */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-medium">Total Amount:</span>
              <span className="text-xl font-bold">
                ${payment.amount?.units || 0}.{String(payment.amount?.nanos || 0).padStart(2, '0').slice(0, 2)}
              </span>
            </div>
            {payment.description && (
              <p className="text-sm text-gray-600 mt-1">{payment.description}</p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Payment Method Selection */}
            <div className="space-y-3">
              <h3 className="font-medium">Payment Method</h3>
              
              <PaymentMethodSelector
                paymentMethods={paymentMethods}
                selectedPaymentMethod={selectedPaymentMethod}
                onSelect={setSelectedPaymentMethod}
                onUseNewCard={() => {
                  setUseNewCard(true);
                  setSelectedPaymentMethod(null);
                }}
                useNewCard={useNewCard}
                onCancelNewCard={() => {
                  setUseNewCard(false);
                  if (paymentMethods.length > 0) {
                    setSelectedPaymentMethod(paymentMethods.find(pm => pm.isDefault) || paymentMethods[0]);
                  }
                }}
              />

              {/* New Card Input */}
              {useNewCard && (
                <div className="p-4 border rounded-md bg-white">
                  <CardElement options={cardElementOptions} />
                </div>
              )}
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2 justify-end pt-4">
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
              )}
              
              <Button
                type="submit"
                disabled={!stripe || isLoading || (!selectedPaymentMethod && !useNewCard)}
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Complete Payment
              </Button>
            </div>
          </form>
        </div>
      </CardContent>
    </Card>
  );
}